import { SliceUserSegmentsSettings } from 'extensions/core/slice-user-segments/front/slice-user-segments-settings.model';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';

import type { UserSegment } from 'shared/models/user-segment.model';

/**
 * Filters components based on the user's segments.
 *
 * Display rules for slice-user-segments:
 * 1. Both lists empty (default slice): show only if user has NO segments.
 * 2. Only inclusion configured: show if user ∈ any included segment.
 * 3. Only exclusion configured: show if user ∉ any excluded segment.
 * 4. Both configured (AND): show if user ∈ any included AND user ∉ any excluded.
 *
 * When userSegments param is undefined or empty, treated as "user has no segments".
 */
export function filterByUserSegments(
  components: IdtComponent[],
  userSegments: UserSegment[] | undefined
): IdtComponent[] {
  if (!components || components.length === 0) {
    return components;
  }

  // Build a map of all components by ID for quick lookup
  const componentMap = new Map<string, IdtComponent>();
  components.forEach((comp) => {
    if (comp.id) {
      componentMap.set(comp.id, comp);
    }
  });

  const idsToRemove = new Set<string>();

  // Helper: Recursively collect all descendant IDs
  const collectDescendantsForRemoval = (componentId: string): void => {
    const comp = componentMap.get(componentId);
    if (!comp) {
      return;
    }

    // If component has children, recursively collect their descendants
    if (comp.children && typeof comp.children === 'object') {
      Object.keys(comp.children).forEach((slotName) => {
        const childIds = comp.children[slotName];
        if (Array.isArray(childIds)) {
          childIds.forEach((childId) => {
            idsToRemove.add(childId);
            collectDescendantsForRemoval(childId);
          });
        }
      });
    }
  };

  // Identify all slice-user-segments components that should be removed
  components.forEach((comp) => {
    if (comp.definitionId === 'slice-user-segments') {
      const resolvedSegments = userSegments ?? [];
      const settings = comp.settings as SliceUserSegmentsSettings;
      const includedSegments = settings.general.userSegments;
      const excludedSegments = settings.general.excludedUserSegments;

      const hasInclusion = includedSegments.length > 0;
      const hasExclusion = excludedSegments.length > 0;

      let shouldRemove: boolean;

      if (!hasInclusion && !hasExclusion) {
        // Default slice: only for users with NO segments
        shouldRemove = resolvedSegments.length > 0;
      } else {
        // AND logic: both conditions must pass
        const isIncluded = !hasInclusion || resolvedSegments.some((name) => includedSegments.includes(name));
        const isNotExcluded = !hasExclusion || !resolvedSegments.some((name) => excludedSegments.includes(name));
        shouldRemove = !(isIncluded && isNotExcluded);
      }

      if (shouldRemove && comp.id) {
        idsToRemove.add(comp.id);
        collectDescendantsForRemoval(comp.id);
      }
    }
  });

  // If nothing to remove, return original array
  if (idsToRemove.size === 0) {
    return components;
  }

  // Filter out removed components
  const filtered = components.filter((comp) => !idsToRemove.has(comp.id));

  // Clean up parent children maps to remove references to removed IDs
  filtered.forEach((comp) => {
    if (comp.children && typeof comp.children === 'object') {
      Object.keys(comp.children).forEach((slotName) => {
        const childIds = comp.children[slotName];
        if (Array.isArray(childIds)) {
          comp.children[slotName] = childIds.filter((childId) => !idsToRemove.has(childId));
        }
      });
    }
  });

  return filtered;
}

